<template>
  <div>
    <new-snapshot-approver-sidebar />
    <b-card no-body class="mb-1">
      <div class="m-2">
        <b-row>
          <b-col
            cols="12"
            md="6"
            class="d-flex align-items-center justify-content-start mb-1 mb-md-0"
          >
            <label class="mb-0">Exibir</label>
            <v-select
              v-model="itemsPerPage"
              dir="ltr"
              :options="itemsPerPageOptions"
              :clearable="false"
              class="per-page-selector d-inline-block mx-50"
              @input="search(currentPage)"
            >
              <template #no-options>Sem resultados</template>
            </v-select>
            <label class="mb-0">itens</label>
          </b-col>
          <b-col cols="12" md="6">
            <div class="d-flex align-items-center justify-content-end">
              <b-button variant="primary" @click.prevent="createApprover">
                <feather-icon icon="PlusCircleIcon" class="mr-50" />
                <span class="text-nowrap">Novo aprovador</span>
              </b-button>
            </div>
          </b-col>
        </b-row>
      </div>

      <b-table
        :items="items"
        :fields="fields"
        :busy="loading"
        primary-key="id"
        responsive
        show-empty
        empty-text="Nenhum aprovador cadastrado"
        class="position-relative"
      >
        <template #cell(created_at)="data">
          {{ data.item.created_at | dateTime }}
        </template>

        <template #cell(actions)="data">
          <b-dropdown variant="link" no-caret right>
            <template #button-content>
              <feather-icon
                icon="MoreVerticalIcon"
                size="16"
                class="align-middle text-body"
              />
            </template>
            <b-dropdown-item @click="removeItem(data.item)">
              <feather-icon icon="TrashIcon" />
              <span class="align-middle ml-50">Remover</span>
            </b-dropdown-item>
          </b-dropdown>
        </template>
      </b-table>

      <div class="mx-2 mb-2">
        <b-row>
          <b-col
            cols="12"
            sm="6"
            class="d-flex align-items-center justify-content-center justify-content-sm-start"
          >
            <span class="text-muted">
              Exibindo {{ pageItemsFrom }} à {{ pageItemsTo }} de {{ totalItems }} itens
            </span>
          </b-col>
          <b-col
            cols="12"
            sm="6"
            class="d-flex align-items-center justify-content-center justify-content-sm-end"
          >
            <b-pagination
              :value="currentPage"
              :total-rows="totalItems"
              :per-page="itemsPerPage"
              first-number
              last-number
              class="mb-0 mt-1 mt-sm-0"
              prev-class="prev-item"
              next-class="next-item"
              @change="search"
            >
              <template #prev-text>
                <feather-icon icon="ChevronLeftIcon" size="18" />
              </template>
              <template #next-text>
                <feather-icon icon="ChevronRightIcon" size="18" />
              </template>
            </b-pagination>
          </b-col>
        </b-row>
      </div>
    </b-card>

    <b-card no-body>
      <b-card-header>
        <b-card-title>Histórico de alterações</b-card-title>
      </b-card-header>

      <b-table
        :items="historyItems"
        :fields="historyFields"
        :busy="loadingHistory"
        primary-key="id"
        responsive
        show-empty
        empty-text="Nenhuma alteração registrada"
        class="position-relative"
      >
        <template #cell(created_at)="data">
          {{ data.item.created_at | dateTime }}
        </template>

        <template #cell(action)="data">
          {{ approverActionLabel(data.item.action) }}
        </template>
      </b-table>

      <div class="mx-2 mb-2">
        <b-row>
          <b-col
            cols="12"
            sm="6"
            class="d-flex align-items-center justify-content-center justify-content-sm-start"
          >
            <span class="text-muted">
              Exibindo {{ historyResult.from || 0 }} à {{ historyResult.to || 0 }} de
              {{ historyResult.total || 0 }} itens
            </span>
          </b-col>
          <b-col
            cols="12"
            sm="6"
            class="d-flex align-items-center justify-content-center justify-content-sm-end"
          >
            <b-pagination
              :value="historyPage"
              :total-rows="historyResult.total || 0"
              :per-page="historyResult.per_page || initialItemsPerPage"
              first-number
              last-number
              class="mb-0 mt-1 mt-sm-0"
              prev-class="prev-item"
              next-class="next-item"
              @change="searchHistory"
            >
              <template #prev-text>
                <feather-icon icon="ChevronLeftIcon" size="18" />
              </template>
              <template #next-text>
                <feather-icon icon="ChevronRightIcon" size="18" />
              </template>
            </b-pagination>
          </b-col>
        </b-row>
      </div>
    </b-card>
  </div>
</template>

<script>
import {
  BCard,
  BCardHeader,
  BCardTitle,
  BRow,
  BCol,
  BButton,
  BTable,
  BDropdown,
  BDropdownItem,
  BPagination,
} from "bootstrap-vue";
import vSelect from "vue-select";
import { mapActions, mapGetters } from "vuex";
import { useToast } from "vue-toastification/composition";
import ToastificationContent from "@core/components/toastification/ToastificationContent";
import useAppConfig from "@core/app-config/useAppConfig";
import { apiErrorToast } from "@/helpers/error_messages";
import { approverActionLabel } from "@/constants/snapshot_change";
import * as sharedTypes from "@/modules/shared/store/types";
import NewSnapshotApproverSidebar from "../components/NewSnapshotApproverSidebar";
import * as types from "../store/types";

export default {
  components: {
    BCard,
    BCardHeader,
    BCardTitle,
    BRow,
    BCol,
    BButton,
    BTable,
    BDropdown,
    BDropdownItem,
    BPagination,
    vSelect,
    NewSnapshotApproverSidebar,
  },
  setup() {
    const { skin } = useAppConfig();

    return { toast: useToast(), skin };
  },
  data() {
    return {
      loading: false,
      loadingHistory: false,
      currentPage: 1,
      itemsPerPage: 10,
      historyPage: 1,
      historyResult: {},
      fields: [
        { key: "name", label: "Usuário" },
        { key: "email", label: "E-mail" },
        { key: "role_name", label: "Perfil" },
        { key: "created_at", label: "Incluído em" },
        { key: "actions", label: "Ações" },
      ],
      historyFields: [
        { key: "created_at", label: "Data" },
        { key: "user_name", label: "Aprovador" },
        { key: "action", label: "Ação" },
        { key: "actor_name", label: "Responsável" },
      ],
    };
  },
  computed: {
    ...mapGetters({
      searchResult: types.APPROVERS_SEARCH_RESULT,
      initialItemsPerPage: sharedTypes.INITIAL_ITEMS_PER_PAGE,
      itemsPerPageOptions: sharedTypes.ITEMS_PER_PAGE_OPTIONS,
    }),
    items() {
      return this.searchResult?.data || [];
    },
    totalItems() {
      return this.searchResult?.total || 0;
    },
    pageItemsFrom() {
      return this.searchResult?.from || 0;
    },
    pageItemsTo() {
      return this.searchResult?.to || 0;
    },
    historyItems() {
      return this.historyResult?.data || [];
    },
  },
  mounted() {
    this.itemsPerPage = this.initialItemsPerPage;
    this.search(1);
    this.searchHistory(1);
  },
  methods: {
    ...mapActions({
      searchApprovers: types.SEARCH_APPROVERS,
      getApproversHistory: types.GET_APPROVERS_HISTORY,
      deleteApprover: types.REMOVE_APPROVER,
      openApproverSidebar: types.OPEN_APPROVER_SIDEBAR,
    }),
    approverActionLabel,
    search(page) {
      this.currentPage = page || 1;
      this.loading = true;
      this.searchApprovers({
        limit: this.itemsPerPage,
        page: this.currentPage,
      })
        .catch((error) => {
          this.toast({
            component: ToastificationContent,
            props: apiErrorToast(
              error,
              "Ocorreu um erro ao carregar os aprovadores. Entre em contato com o setor de TI."
            ),
          });
        })
        .finally(() => {
          this.loading = false;
        });
    },
    searchHistory(page) {
      this.historyPage = page || 1;
      this.loadingHistory = true;
      this.getApproversHistory({
        limit: this.initialItemsPerPage,
        page: this.historyPage,
      })
        .then((response) => {
          this.historyResult = response.data;
        })
        .catch((error) => {
          this.toast({
            component: ToastificationContent,
            props: apiErrorToast(
              error,
              "Ocorreu um erro ao carregar o histórico. Entre em contato com o setor de TI."
            ),
          });
        })
        .finally(() => {
          this.loadingHistory = false;
        });
    },
    refresh() {
      this.search(this.currentPage);
      this.searchHistory(1);
    },
    createApprover() {
      this.openApproverSidebar({
        saveAction: this.refresh,
      });
    },
    removeItem(item) {
      this.$swal({
        title: "Tem certeza?",
        text: `${item.name} não poderá mais aprovar alterações de snapshot.`,
        icon: "warning",
        showCancelButton: true,
        confirmButtonText: "Remover",
        cancelButtonText: "Cancelar",
        background: `${this.skin === "dark" ? "#283046" : ""}`,
        customClass: {
          confirmButton: "btn btn-primary",
          cancelButton: "btn btn-secondary ml-1",
          htmlContainer: `${this.skin === "dark" ? "text-white" : ""}`,
        },
      }).then((result) => {
        if (result.value) {
          this.deleteApprover(item.id)
            .then(() => {
              this.toast({
                component: ToastificationContent,
                props: {
                  title: "Sucesso",
                  text: `${item.name} foi removido dos aprovadores.`,
                  icon: "CoffeeIcon",
                  variant: "success",
                },
              });
              this.refresh();
            })
            .catch((error) => {
              this.toast({
                component: ToastificationContent,
                props: apiErrorToast(
                  error,
                  "Ocorreu um erro ao remover o aprovador. Entre em contato com o setor de TI."
                ),
              });
            });
        }
      });
    },
  },
};
</script>

<style lang="scss" scoped>
.per-page-selector {
  width: 90px;
}
</style>

<style lang="scss">
@import "@core/scss/vue/libs/vue-select.scss";
</style>
